import {
  useReducer,
  useCallback,
  createContext,
  useEffect,
  useRef,
  useState
} from 'react';
import { debounce } from 'lodash';
import { INIT_STATE, INIT_CONTEXT } from './constants';

// Types
// ----------------------------------------------------------------------------

const ADD_PARALLAX = 'ADD_PARALLAX';
const REMOVE_PARALLAX = 'REMOVE_PARALLAX';
const ENABLE = 'ENABLE';
const DISABLE = 'DISABLE';

// Reducer
// ----------------------------------------------------------------------------

const ParallaxContext = createContext(INIT_CONTEXT);

const reducer = (state, { type, payload }) => {
  switch (type) {
    case ADD_PARALLAX:
      return {
        ...state,
        parallaxes: [
          ...state.parallaxes.filter((x) => x.id !== payload.id),
          payload
        ].sort((a, b) => a.blockTop - b.blockTop)
      };

    case REMOVE_PARALLAX:
      return {
        ...state,
        parallaxes: state.parallaxes.filter((x) => x.id !== payload.id)
      };

    case ENABLE:
      return {
        ...state,
        isEnabled: true
      };

    case DISABLE:
      return {
        ...state,
        isEnabled: false
      };

    default:
      return state;
  }
};

export const useParallaxContextValue = () => {
  const [state, dispatch] = useReducer(reducer, INIT_STATE);
  const [isResizing, setIsResizing] = useState(false);
  const childrenRef = useRef();
  const scrollRef = useRef(0);

  const setChildrenRef = useCallback(
    (node) => {
      const handleScroll = () => {
        scrollRef.current = node.scrollTop;
      };

      if (childrenRef.current) {
        childrenRef.current.removeEventListener('scroll', handleScroll);
      }

      if (node) {
        node.addEventListener('scroll', handleScroll);
      }

      childrenRef.current = node;
    },
    [scrollRef]
  );

  const addParallax = useCallback(
    (parallax) =>
      dispatch({
        type: ADD_PARALLAX,
        payload: parallax
      }),
    []
  );

  const removeParallax = useCallback(
    (parallax) =>
      dispatch({
        type: REMOVE_PARALLAX,
        payload: parallax
      }),
    []
  );

  const enable = useCallback(() => dispatch({ type: ENABLE }), []);
  const disable = useCallback(() => dispatch({ type: DISABLE }), []);

  useEffect(() => {
    const endResize = debounce(() => {
      setIsResizing(false);
    }, 500);
    const startResize = () => {
      setIsResizing(true);
      endResize();
    };

    window.addEventListener('resize', startResize);

    return () => {
      window.removeEventListener('resize', startResize);
    };
  }, []);

  const value = {
    ...state,
    addParallax,
    removeParallax,
    enable,
    disable,
    scrollRef,
    isResizing,
    setChildrenRef,
    childrenRef
  };

  return value;
};

export default ParallaxContext;
